import { Suspense, lazy, useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Navigate } from 'react-router-dom'
import { useApi } from '../lib/useApi'
import { useAuth } from '../lib/auth.jsx'
import { useTheme } from '../lib/useTheme.js'
import { isEnabled } from '../lib/features.js'
import { usePullToRefresh } from '../lib/usePullToRefresh'
import { useToast } from '../lib/toast.jsx'
import { useKeyboardShortcuts } from '../lib/useKeyboardShortcuts.js'
import { useTimeOfDayTheme } from '../lib/useTimeOfDayTheme.js'
import { useSessionWatch } from '../lib/useSessionWatch.js'
import HeaderBar from '../components/UI/HeaderBar.jsx'
import BottomTabBar from '../components/Mobile/BottomTabBar.jsx'
import QuickDock from '../components/QuickDock/QuickDock.jsx'
import { SkeletonRow } from '../components/UI/Skeleton.jsx'
import EgoQuoteBanner from '../components/Themes/EgoQuoteBanner.jsx'
import DailyGoals from '../components/TodayFocus/DailyGoals.jsx'
import HeuteForm from '../components/TodayFocus/HeuteForm.jsx'
import SRankSection from '../components/Health/SRankSection.jsx'
import StreakHeatmap from '../components/UI/StreakHeatmap.jsx'
import QuestsPanel from '../components/Quests/QuestsPanel.jsx'
import KanbanPanel from '../components/Kanban/KanbanPanel.jsx'

// Code-split the heavy / less-used modules.
const MacrobotPanel       = lazy(() => import('../components/TodayFocus/MacrobotPanel.jsx'))
const BookReflection      = lazy(() => import('../components/TodayFocus/BookReflection.jsx'))
const Supplements         = lazy(() => import('../components/TodayFocus/Supplements.jsx'))
const CaffeinePanel       = lazy(() => import('../components/Health/CaffeinePanel.jsx'))
const TrainingCoachPanel  = lazy(() => import('../components/Coach/TrainingCoachPanel.jsx'))
const DatabaseViewer      = lazy(() => import('../components/Database/DatabaseViewer.jsx'))
const DataPanel           = lazy(() => import('../components/DataPanel/DataPanel.jsx'))
const FeatureToggles      = lazy(() => import('../components/Settings/FeatureToggles.jsx'))
const ProfileSettings     = lazy(() => import('../components/Settings/ProfileSettings.jsx'))
const DailySummary        = lazy(() => import('../components/TodayFocus/DailySummary.jsx'))
const WeeklySparklines    = lazy(() => import('../components/TodayFocus/WeeklySparklines.jsx'))
const CommandPalette      = lazy(() => import('../components/Command/CommandPalette.jsx'))
const TemplatesPanel      = lazy(() => import('../components/Quests/TemplatesPanel.jsx'))

const Loading = () => <div className="p-2"><SkeletonRow count={2} /></div>

const MISC_SCHEMAS = {
  body:     { title: 'Body Measure', table: 'body_measurements', fields: [
    { key: 'date',   label: 'Datum',   type: 'date' },
    { key: 'weight', label: 'Weight',  type: 'number' },
    { key: 'chest',  label: 'Chest',   type: 'number' },
    { key: 'waist',  label: 'Waist',   type: 'number' },
    { key: 'hips',   label: 'Hips',    type: 'number' },
    { key: 'arm',    label: 'Arm',     type: 'number' },
    { key: 'thigh',  label: 'Thigh',   type: 'number' },
    { key: 'notes',  label: 'Notes',   type: 'text' },
  ] },
  wishlist: { title: 'Wishlist', table: 'wishlist', fields: [
    { key: 'title',    label: 'Titel',    type: 'text',   required: true },
    { key: 'url',      label: 'URL',      type: 'text' },
    { key: 'price',    label: 'Preis',    type: 'number' },
    { key: 'priority', label: 'Priority', type: 'number' },
    { key: 'notes',    label: 'Notes',    type: 'text' },
  ] },
  learning: { title: 'Learning Log', table: 'learning_log', fields: [
    { key: 'topic',        label: 'Topic',     type: 'text', required: true },
    { key: 'duration_min', label: 'Min',       type: 'number' },
    { key: 'notes',        label: 'Notes',     type: 'text' },
  ] },
  finance:  { title: 'Finance', table: 'finance_entries', fields: [
    { key: 'category', label: 'Kategorie', type: 'text' },
    { key: 'amount',   label: 'Betrag',    type: 'number', required: true },
    { key: 'currency', label: 'Currency',  type: 'select', options: ['EUR','USD','VND','CHF'] },
    { key: 'notes',    label: 'Notes',     type: 'text' },
  ] },
}

export default function HealthDashboard() {
  const api = useApi()
  const { user } = useAuth()
  const [tab, setTab] = useState('heute')
  const [coachOpen, setCoachOpen] = useState(false)
  const [dbOpen, setDbOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.get('/profile'),
    enabled: !!user,
  })

  useTheme(profile?.theme)
  useTimeOfDayTheme(profile?.time_theme_enabled)   // optional auto-theme
  useSessionWatch()

  const toast = useToast()
  usePullToRefresh(() => toast.push({ kind: 'info', title: 'Aktualisiert', ttl: 1500 }))

  useKeyboardShortcuts({
    openPalette: () => setPaletteOpen(true),
    openCoach:   on => on?.coach ? () => setCoachOpen(true) : null,
    onTab:       (id) => setTab(id),
  })

  // Run quest templates once per app open (idempotent)
  useEffect(() => {
    if (!user) return
    api.post('/quests/templates/run').catch(() => { /* no templates / 503 */ })
  }, [user, api])

  if (!isLoading && profile && profile.onboarded === false) {
    return <Navigate to="/onboarding" replace />
  }

  const on = (id) => isEnabled(profile, id)
  const heuteHasContent =
    on('daily_goals') || on('heute_form') || on('srank') || on('streak') ||
    on('macrobot')   || on('supplements') || on('reflection') || on('caffeine')

  return (
    <div className="min-h-screen pb-24 md:pb-8 bg-app">
      <a href="#main" className="skip-link">Skip to content</a>
      <HeaderBar profile={profile} onOpenDb={on('db_viewer') ? () => setDbOpen(true) : null} />

      <div className="md:grid md:grid-cols-[64px_1fr] gap-4 px-3 md:px-6 max-w-7xl mx-auto">
        <QuickDock tab={tab} setTab={setTab}
                   onCoach={on('coach') ? () => setCoachOpen(true) : null}
                   onDb={on('db_viewer') ? () => setDbOpen(true) : null} />

        <main id="main" role="main" className="space-y-6 mt-4 md:mt-6">
          <EgoQuoteBanner theme={profile?.theme} />

          {tab === 'heute' && (
            <div className="space-y-6">
              {!heuteHasContent && <EmptyTab tab="heute" />}
              {heuteHasContent && (
                <Suspense fallback={null}><DailySummary /></Suspense>
              )}
              {on('daily_goals') && <DailyGoals />}
              {on('heute_form')  && <HeuteForm />}
              {on('srank')       && <SRankSection />}
              {on('streak')      && <StreakHeatmap />}
              <Suspense fallback={<Loading />}>
                {on('streak')      && <WeeklySparklines />}
                {on('macrobot')    && <MacrobotPanel />}
                {on('supplements') && <Supplements />}
                {on('reflection')  && <BookReflection />}
                {on('caffeine')    && <CaffeinePanel />}
              </Suspense>
            </div>
          )}

          {tab === 'board' && (
            <div className="space-y-6">
              {!on('quests') && !on('kanban') && <EmptyTab tab="board" />}
              {on('quests') && <QuestsPanel />}
              {on('quests') && (
                <Suspense fallback={null}><TemplatesPanel /></Suspense>
              )}
              {on('kanban') && <KanbanPanel />}
            </div>
          )}

          {tab === 'daten' && (
            <Suspense fallback={<Loading />}>
              {on('db_viewer') ? <DatabaseViewer inline /> : <EmptyTab tab="daten" reason="DatabaseViewer-Modul aus" />}
            </Suspense>
          )}

          {tab === 'mehr' && (
            <Suspense fallback={<Loading />}>
              <div className="space-y-6">
                <ProfileSettings />
                {on('body')     && <DataPanel {...MISC_SCHEMAS.body} />}
                {on('wishlist') && <DataPanel {...MISC_SCHEMAS.wishlist} />}
                {on('learning') && <DataPanel {...MISC_SCHEMAS.learning} />}
                {on('finance')  && <DataPanel {...MISC_SCHEMAS.finance} />}
                <FeatureToggles />
              </div>
            </Suspense>
          )}
        </main>
      </div>

      {on('coach') && (
        <>
          <CoachFab onClick={() => setCoachOpen(true)} />
          <Suspense fallback={null}>
            <TrainingCoachPanel open={coachOpen} onClose={() => setCoachOpen(false)} />
          </Suspense>
        </>
      )}

      {on('db_viewer') && dbOpen && (
        <Suspense fallback={null}>
          <DatabaseViewer drawer onClose={() => setDbOpen(false)} />
        </Suspense>
      )}

      <Suspense fallback={null}>
        <CommandPalette
          open={paletteOpen}
          onClose={() => setPaletteOpen(false)}
          onSwitchTab={(t) => setTab(t)}
          onOpenCoach={on('coach') ? () => setCoachOpen(true) : null}
          onOpenDb={on('db_viewer') ? () => setDbOpen(true) : null}
        />
      </Suspense>

      <BottomTabBar tab={tab} setTab={setTab} />
    </div>
  )
}

function EmptyTab({ tab, reason }) {
  return (
    <div className="border border-dashed border-card rounded-themed p-6 text-center space-y-2">
      <div className="label-mini">{tab}</div>
      <div className="opacity-70 text-sm">
        {reason || 'Kein Modul aktiv für diesen Tab.'} Öffne <b>Mehr → Module</b> und aktiviere was du brauchst.
      </div>
    </div>
  )
}

function CoachFab({ onClick }) {
  return (
    <button onClick={onClick}
            aria-label="Open Coach"
            className="md:hidden fixed right-4 bottom-20 w-14 h-14 rounded-full bg-accent grid place-items-center shadow-themed animate-pulse-glow z-30">
      <span className="font-display text-lg">⚔</span>
    </button>
  )
}
